import { Divider } from "antd";
import { type JSX, useCallback } from "react";
import { TableRowActionsMenu } from "~components/DataTable/components/TableRowActionsMenu/TableRowActionsMenu.tsx";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import ShowRosterFixtureAction from "~pages/Fixtures/FixtureListPage/components/Actions/ShowRosterFixtureAction";
import type { ColumnComponentProperties, FixtureTransformed } from "../../types";
import {
  BroadcastsFixtureAction,
  DeleteFixtureAction,
  LockUnlockFixtureAction,
  ModalEditFixtureAction,
  ResetFixtureAction,
  ViewFixtureAction,
} from "../Actions";

interface IActionsColumnProperties extends ColumnComponentProperties {
  massEditMode: boolean;
  editFixtureId?: string;
  onModalEditClicked: (fixture: FixtureTransformed) => void;
  onLockUnlockClicked?: (fixtureId: string) => void;
}

type ActionMenuItem = {
  key: string;
  label: JSX.Element;
  disabled?: boolean;
};

export const ActionsColumn = (properties: IActionsColumnProperties) => {
  const { hasPermission } = useUserPermissions();
  const hasPermissionToUpdateFixture = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const hasPermissionToDeleteFixture = hasPermission(PermissionNames.FIXTURE, PermissionOperations.DELETE);
  const hasPermissionToUpdateFixtureSettings = hasPermission(PermissionNames.FIXTURE_SETTINGS, PermissionOperations.UPDATE);
  const sportConfiguration = useSportConfiguration();

  const advancedActionsMenuItems: ActionMenuItem[] = [];

  const onModalEditModeClickedHandler = useCallback(() => {
    if (properties.onModalEditClicked) {
      properties.onModalEditClicked(properties.value as FixtureTransformed);
    }
  }, [properties]);

  if (hasPermissionToUpdateFixture) {
    advancedActionsMenuItems.push(
      {
        key: "view",
        label: <ViewFixtureAction fixtureId={properties.value.fixtureId} />,
      },
      {
        key: "edit",
        label: <ModalEditFixtureAction onModalEditModeClickedHandler={onModalEditModeClickedHandler} />,
        disabled: properties.value.locked as boolean,
      },
      {
        key: "broadcasts",
        label: <BroadcastsFixtureAction fixtureId={properties.value.fixtureId} />,
      },
    );
  }

  if (hasPermissionToUpdateFixtureSettings || hasPermissionToDeleteFixture) {
    advancedActionsMenuItems.push({
      key: "divider-1",
      label: <Divider style={{ margin: 0 }} />,
    });
  }

  if (hasPermissionToUpdateFixtureSettings) {
    if (sportConfiguration.competitorType === CompetitorTypes.Entity) {
      advancedActionsMenuItems.push({
        key: "roster",
        label: <ShowRosterFixtureAction fixtureId={properties.value.fixtureId} />,
      });
    }
    advancedActionsMenuItems.push({
      key: "lock-unlock",
      label: <LockUnlockFixtureAction fixture={properties.value} onLockUnlockClicked={() => {}} />,
    });
    advancedActionsMenuItems.push({
      key: "reset",
      label: <ResetFixtureAction fixtureId={properties.value.fixtureId} />,
      disabled: properties.value.locked as boolean,
    });
  }

  if (hasPermissionToDeleteFixture) {
    advancedActionsMenuItems.push({
      key: "delete",
      label: <DeleteFixtureAction fixture={properties.value} />,
      disabled: properties.value.locked as boolean,
    });
  }

  return <TableRowActionsMenu tableRowActionsMenuItems={advancedActionsMenuItems} />;
};
